// Data-plane (React-free) persistence descriptor for expected-action
// annotations. Kept separate from the view adapter so the persistence store can
// import it without pulling React component modules into its graph.

import type { ExpectedActionDraft } from '../expectedAction/expectedAction.interface.js';
import {
  parseExpectedActionsStrict,
  serializeExpectedActions,
} from '../expectedAction/expectedActionPayload.js';

import type {
  AnnotationPersistenceDef,
  AnnotationSerializeInput,
  SavedAnnotation,
} from './annotationType.interface.js';

/** Stable discriminator for expected-action sets and payloads. */
export const EXPECTED_ACTION_KIND = 'expected-action';

/** Fixed name of the schema every expected-action set is bound to. */
export const EXPECTED_ACTION_SCHEMA_NAME = 'expected-action';

const EXPECTED_ACTION_SCHEMA_DESCRIPTION =
  'Auto-created schema for expected-action annotations produced by the ' +
  'Datasets annotation editor.';

// The uploaded payload is our own JSON blob, so the schema definition is a
// nominal contract. It documents the payload shape for anyone inspecting the
// schema catalog.
const EXPECTED_ACTION_SCHEMA_DEFINITION = JSON.stringify({
  type: 'expected-action',
  description:
    'Per-file expected actions. See expectedActionPayload.ts for the payload.',
});

/**
 * Persistence contract for expected-action annotations. Casts the opaque
 * registry `draft` back to `ExpectedActionDraft` only inside these adapters, so
 * the rest of the system stays schema-agnostic.
 */
export const EXPECTED_ACTION_PERSISTENCE: AnnotationPersistenceDef = {
  kind: EXPECTED_ACTION_KIND,
  schemaName: EXPECTED_ACTION_SCHEMA_NAME,
  schemaDescription: EXPECTED_ACTION_SCHEMA_DESCRIPTION,
  schemaDefinition: EXPECTED_ACTION_SCHEMA_DEFINITION,
  serialize(input: AnnotationSerializeInput): string {
    return serializeExpectedActions({
      actions: input.actions.map((action) => ({
        turnId: action.turnId,
        afterEventIndex: action.afterEventIndex,
        draft: action.draft as ExpectedActionDraft,
      })),
      fileId: input.fileId,
      sourceContentHash: input.sourceContentHash,
      labeledBy: input.labeledBy,
    });
  },
  parse(text: string, sourceFileId?: string): SavedAnnotation[] {
    return parseExpectedActionsStrict(text, sourceFileId);
  },
};
